import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Camera, ImagePlus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOG_TYPES, LOG_FIELDS, uploadLogPhoto } from "./logTypes";
import { CameraCapture, hasCamera } from "@/components/CameraCapture";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

/** Log one activity for one or many children. */
export function QuickLogDialog({ open, type, children: kids, onClose, onSaved }) {
  const [data, setData] = useState({});
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const choosePhoto = (f) => { if (preview) URL.revokeObjectURL(preview); setPhoto(f || null); setPreview(f ? URL.createObjectURL(f) : ""); };
  const meta = LOG_TYPES[type] || LOG_TYPES.note;
  const fields = LOG_FIELDS[type] || [];

  useEffect(() => { if (open) { setData({}); setNote(""); choosePhoto(null); } }, [open, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let photo_id = "";
      if (photo) photo_id = await uploadLogPhoto(photo);
      await api.post("/daycare/log", { student_ids: kids.map((k) => k.id), type, data, note, photo_id });
      toast.success(`${meta.label} logged for ${kids.length === 1 ? kids[0].full_name.split(" ")[0] : `${kids.length} children`}`);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="quick-log-dialog">
        <form onSubmit={save} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><span className={`h-8 w-8 rounded-lg border inline-flex items-center justify-center ${meta.cls}`}><meta.Icon className="h-4 w-4" /></span>{meta.label}</DialogTitle>
            <DialogDescription>{kids.map((k) => k.full_name.split(" ")[0]).join(", ")}</DialogDescription>
          </DialogHeader>
          {fields.map((f) => (
            <div key={f.key}>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-slate-500">{f.label}</div>
              {f.options && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {f.options.map((o) => (
                    <button key={o} type="button" onClick={() => setData((d) => ({ ...d, [f.key]: o }))} data-testid={`log-opt-${f.key}-${o.replace(/\W+/g, "-")}`}
                      className={`rounded-full border px-3 py-1.5 text-sm capitalize transition ${data[f.key] === o ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{o}</button>
                  ))}
                </div>
              )}
              {f.text && <input value={data[f.key] || ""} onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))} className={inputCls} data-testid={`log-field-${f.key}`} placeholder={f.options ? "Or type your own…" : ""} />}
            </div>
          ))}
          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-slate-500">Note for parents (optional)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={600} data-testid="log-note"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </div>
          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-slate-500">Photo (optional)</div>
            <div className="flex items-center gap-3">
              {preview && (
                <div className="relative shrink-0" data-testid="log-photo-preview">
                  <img src={preview} alt="Selected" className="h-16 w-16 rounded-xl object-cover ring-1 ring-slate-200" />
                  <button type="button" onClick={() => choosePhoto(null)} className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900 p-0.5 text-white" aria-label="Remove photo" data-testid="log-photo-remove"><X className="h-3 w-3" /></button>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { choosePhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="log-photo-input" />
              <div className="flex flex-wrap gap-2">
                {hasCamera() && (
                  <button type="button" onClick={() => setCameraOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 h-9 text-sm font-medium text-white hover:shadow" data-testid="log-photo-camera-btn">
                    <Camera className="h-4 w-4" /> {photo ? "Retake" : "Take photo"}
                  </button>
                )}
                <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 h-9 text-sm text-slate-700 hover:bg-slate-50" data-testid="log-photo-btn">
                  <ImagePlus className="h-4 w-4" /> {photo ? "Choose another" : "Choose photo"}
                </button>
              </div>
            </div>
          </div>
          <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={choosePhoto} title="Photo for parents" />
          <DialogFooter>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:shadow disabled:opacity-60" data-testid="log-save-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
